import * as React from "react";
import { useMemo, useContext } from "react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useQuery } from "@tanstack/react-query";
import fetchAdhocReportValues from "report/report-render-page/fetchAdhocReportValues";
import ErrorBoundary from "components/errors/ErrorBoundary";
import ReportRenderErrorPage from "report/report-render-page/ReportRenderErrorPage";
import useRoutingQuery from "routing/useQuery";
import { timeRangeFromQueryParameter } from "views/logic/TimeRange";
import RenderedReport from "report/common/RenderedReport";
import useReport from "report/report-history-page/useReport";
import useWidgetDetails from "report/hooks/useWidgetDetails";
import WidgetDetailsContext from "report/report-creation/WidgetDetailsContext";
import WidgetResultContext from "report/report-creation/WidgetResultContext";
import PDFExportThemeProvider from "common/providers/PDFExportThemeProvider";
import ReportRenderPageResults from "./ReportRenderPageResults";
const extractTimeRangeOverride = (query) => "rangetype" in query ? timeRangeFromQueryParameter(query) : void 0;
const referenceTimeFromQueryParameter = (query) => moment(query.now);
const extractNow = (query) => "now" in query ? referenceTimeFromQueryParameter(query) : void 0;
const WidgetDetailsProvider = ({
  children = void 0,
  widgets
}) => {
  const { data: widgetDetails, isLoading } = useWidgetDetails(widgets);
  const widgetDetailsContextValue = useMemo(
    () => ({
      ...widgetDetails,
      isLoading
    }),
    [isLoading, widgetDetails]
  );
  return isLoading ? null : /* @__PURE__ */ React.createElement(WidgetDetailsContext.Provider, { value: widgetDetailsContextValue }, children);
};
const WidgetResultProvider = ({
  widgetId,
  dashboardId,
  children
}) => {
  const results = useContext(ReportRenderPageResults);
  const widgetResult = useMemo(
    () => {
      var _a;
      return (_a = results == null ? void 0 : results.find((result) => result.dashboard_id === dashboardId && result.widget_id === widgetId)) == null ? void 0 : _a.result;
    },
    [dashboardId, results, widgetId]
  );
  return /* @__PURE__ */ React.createElement(WidgetResultContext.Provider, { value: widgetResult }, children);
};
const queryKeyForFetch = (id, report, now) => report && "id" in report ? ["report.values", id, report == null ? void 0 : report.parameterValues, now == null ? void 0 : now.toISOString()] : ["report.values.new", id, report == null ? void 0 : report.parameterValues, now == null ? void 0 : now.toISOString()];
const fetchValues = (report, parameterValues, timerangeOverride, now) => fetchAdhocReportValues(
  report.widgets.map(({ dashboardId: dashboard_id, widgetId: dashboard_widget_id }) => ({
    dashboard_id,
    dashboard_widget_id
  })),
  parameterValues,
  timerangeOverride,
  now
);
const useReportResults = (report, now, timerangeOverride) => {
  const reportId = useMemo(() => uuidv4(), [report]);
  const queryKey = useMemo(() => queryKeyForFetch(reportId, report, now), [now, report, reportId]);
  return useQuery({
    queryKey,
    queryFn: () => fetchValues(report, report == null ? void 0 : report.parameterValues, timerangeOverride, now),
    enabled: !!report
  });
};
const RenderReport = ({ reportId }) => {
  const query = useRoutingQuery();
  const timerangeOverride = useMemo(() => extractTimeRangeOverride(query), [query]);
  const now = useMemo(() => extractNow(query), [query]);
  const { data: report, isInitialLoading, error: reportError } = useReport(reportId);
  const {
    data: reportResults,
    isLoading,
    isError,
    error: resultsError
  } = useReportResults(report, now, timerangeOverride);
  if (isError || reportError) {
    return /* @__PURE__ */ React.createElement(ReportRenderErrorPage, { error: reportError != null ? reportError : resultsError });
  }
  if (isInitialLoading || isLoading) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(ReportRenderPageResults.Provider, { value: reportResults }, /* @__PURE__ */ React.createElement(WidgetDetailsProvider, { widgets: report.widgets }, /* @__PURE__ */ React.createElement(RenderedReport, { report, widgetResultProvider: WidgetResultProvider })));
};
const ReportRenderPage = () => {
  const { reportId } = useParams();
  return /* @__PURE__ */ React.createElement(ErrorBoundary, { FallbackComponent: ReportRenderErrorPage }, /* @__PURE__ */ React.createElement(PDFExportThemeProvider, null, /* @__PURE__ */ React.createElement(RenderReport, { reportId })));
};
export default ReportRenderPage;
